import { Dropdown, Search } from '@cubeyou-team/hx-react-components';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import {
  DATA_SOURCE_IDS,
  FUSION_LABELS,
  FUSION_TYPES,
} from '@discover-frontend/constants';
import {
  getTrialFeatureForFusionSource,
  isTrialRestrictedFusionSource,
  TRIAL_RESTRICTED_FUSION_SOURCES,
  useTrialPaywall,
} from 'src/modules/trial-paywall';
import { getSavedQueries } from 'src/utils';
import { getSources, hasSpecificFusion } from 'src/utils/AuthHelperMethods';
import EntityInlineSearchResults from './EntityInlineSearchResults';
import { fetchSavedQueries, fetchSearchResults } from './request';
import { StyledEntityInlineSearchContainer } from './StyledEntityInlineSearch';

const EntityInlineSearch = ({
  currentEntityLabel,
  index,
  onClickOutsideSearch,
  onEntityUpdate,
  onRequireMissingItemRequest,
}) => {
  const folders = useSelector((state) => state.repository.folders);
  const dataSource = useSelector((state) => state.queryBuilder.dataSource);
  const { guardFeature, isTrial } = useTrialPaywall();
  const [inputValue, setInputValue] = useState('');
  const [isSearchResultsLoading, setIsSearchResultsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSource, setSelectedSource] = useState('all');

  function handleSearchKeyDown(e) {
    if (e.keyCode === 27) onClickOutsideSearch();

    if (e.keyCode === 40) {
      e.preventDefault();
      const list = document.getElementsByClassName(
        'inline-search-results-container'
      )[0];
      list && list.firstChild.focus();
    }
  }

  const mounted = useRef(true);
  useLayoutEffect(() => () => (mounted.current = false), []);

  useEffect(() => {
    if (!mounted.current || inputValue === '') {
      setIsSearchResultsLoading(false);
      return;
    }

    const searchSources =
      selectedSource !== 'all' ? selectedSource : getSources(dataSource);

    const updateResults = (results) => {
      if (mounted.current) {
        setIsSearchResultsLoading(false);
        setSearchResults(results);
      }
    };

    const handleSearch = async () => {
      setIsSearchResultsLoading(true);
      try {
        const { response: baseResponse, error: baseError } =
          await fetchSearchResults(inputValue, searchSources);

        if (baseError || inputValue === '') return;

        const combinedData = [...baseResponse.data];

        if (['all', 'saved-queries'].includes(selectedSource)) {
          const { response: savedResponse, error: savedError } =
            await fetchSavedQueries(inputValue, dataSource);
          if (!savedError && inputValue !== '') {
            combinedData.push(...getSavedQueries(savedResponse.data, folders));
          }
        }

        updateResults(combinedData);
      } catch (err) {
        setIsSearchResultsLoading(false);
      }
    };

    handleSearch();
  }, [inputValue, selectedSource, folders, dataSource]);

  const sources = useMemo(() => {
    const filters = [
      {
        id: 'all',
        label: 'All',
      },
      {
        id: dataSource,
        label: 'Discover',
      },
    ];

    if (dataSource === DATA_SOURCE_IDS.TWITTER) {
      Object.keys(FUSION_TYPES).forEach((key) => {
        if (hasSpecificFusion(FUSION_TYPES[key]))
          filters.push({ id: FUSION_TYPES[key], label: FUSION_LABELS[key] });
      });
    }

    filters.push({
      id: 'saved-queries',
      label: 'Saved',
    });

    return filters;
  }, [dataSource]);

  const handleSourceChange = useCallback(
    (value) => {
      const feature = getTrialFeatureForFusionSource(value);
      if (feature) {
        guardFeature(feature, () => setSelectedSource(value));
        return;
      }
      setSelectedSource(value);
    },
    [guardFeature]
  );

  const handleEntitySelect = useCallback(
    (item) => {
      if (isTrial && isTrialRestrictedFusionSource(item.source)) return;
      onEntityUpdate(item);
    },
    [isTrial, onEntityUpdate]
  );

  return (
    <StyledEntityInlineSearchContainer>
      <Dropdown
        content={
          <EntityInlineSearchResults
            inputValue={inputValue}
            isSearchResultsLoading={isSearchResultsLoading}
            results={searchResults}
            onEntitySelect={handleEntitySelect}
            onRequireMissingItemRequest={onRequireMissingItemRequest}
            initialSource={selectedSource}
            sources={sources}
            onSourceChange={handleSourceChange}
            disabledItemSources={
              isTrial ? [...TRIAL_RESTRICTED_FUSION_SOURCES] : undefined
            }
          />
        }
        position="left"
        fullWidth
        isOpen
        onClickOutside={onClickOutsideSearch}
      >
        <Search
          data-test-id="inline-search-input"
          delay={500}
          tabIndex={index}
          autoFocus
          initialValue={currentEntityLabel}
          onSearchChange={(q) => setInputValue(q)}
          placeholder="Search"
          size="small"
          className="hx-inline-search-input"
          onKeyDown={handleSearchKeyDown}
        />
      </Dropdown>
    </StyledEntityInlineSearchContainer>
  );
};

EntityInlineSearch.propsType = {
  currentEntityLabel: PropTypes.string,
  onInputBlur: PropTypes.func,
  onEntityUpdate: PropTypes.func,
};

export default EntityInlineSearch;
